import {
  ExportToRuntimeStackResponse,
  ExportToRuntimeStackRequest,
  FloorplanRevision,
  GenerateFloorplanResponse,
  GenerateFloorplanRequest,
  GenerateTracksResponse,
  GenerateTracksRequest,
} from '@thive/fddm-types';

import {
  convertFloorplanRevisionToExportToRuntimeStackRequest,
  convertFloorplanRevisionToGenerateFloorplanRequest,
  convertFloorplanRevisionToGenerateTracksRequest,
} from '@/modules/floorplanV2/helpers';
import { getConfig } from '@/modules/common/helpers/config';
import { API_TIMEOUTS } from '../constant';
import { BaseConnector } from '@/modules/api/BaseConnector';
import { IFloorplanServiceConnector } from './IFloorplanServiceConnector';

export class FloorplanServiceConnector extends BaseConnector implements IFloorplanServiceConnector {
  private static instance: FloorplanServiceConnector;
  private static baseUrl = getConfig().api.floorplan.url;
  private static scopes = [getConfig().api.floorplan.scope];

  public static getInstance(): IFloorplanServiceConnector {
    if (!FloorplanServiceConnector.instance) {
      FloorplanServiceConnector.instance = new FloorplanServiceConnector();
    }
    return FloorplanServiceConnector.instance;
  }

  private constructor() {
    super(
      FloorplanServiceConnector.baseUrl,
      FloorplanServiceConnector.scopes,
      API_TIMEOUTS.floorplan,
    );
  }

  public async validateFloorplan(
    floorplanRevision: FloorplanRevision,
  ): Promise<GenerateFloorplanResponse> {
    const payload = convertFloorplanRevisionToGenerateFloorplanRequest(floorplanRevision, true);
    return this.post<GenerateFloorplanResponse, GenerateFloorplanRequest>(
      '/generateFloorplan',
      payload,
      {
        requiredErrorMessage: 'Failed to validate floorplan',
      },
    );
  }

  public async generateTracks(
    floorplanRevision: FloorplanRevision,
  ): Promise<GenerateTracksResponse> {
    const payload = convertFloorplanRevisionToGenerateTracksRequest(floorplanRevision);
    return this.post<GenerateTracksResponse, GenerateTracksRequest>('/generateTracks', payload, {
      requiredErrorMessage: 'Failed to generate tracks',
      config: { timeout: 600000 }, // Set timeout to 10 minutes for track generation
    });
  }

  public async generateFloorplan(
    floorplanRevision: FloorplanRevision,
  ): Promise<GenerateFloorplanResponse> {
    const payload = convertFloorplanRevisionToGenerateFloorplanRequest(floorplanRevision, false);

    return this.post<GenerateFloorplanResponse, GenerateFloorplanRequest>(
      '/generateFloorplan',
      payload,
      {
        requiredErrorMessage: 'Failed to generate floorplan',
      },
    );
  }
  public async exportToRuntimeStack(
    floorplanRevision: FloorplanRevision,
    generateTracks: boolean,
    generateProximityMatrix: boolean,
    generateTravelDurationCostMatrix: boolean,
  ): Promise<ExportToRuntimeStackResponse> {
    const payload = convertFloorplanRevisionToExportToRuntimeStackRequest(
      floorplanRevision,
      generateTracks,
      generateProximityMatrix,
      generateTravelDurationCostMatrix,
    );
    return this.post<ExportToRuntimeStackResponse, ExportToRuntimeStackRequest>(
      '/exportToRuntimeStack',
      payload,
      {
        requiredErrorMessage: 'Failed to export to runtime stack',
        config: { timeout: 600000 }, // Set timeout to 10 minutes for runtime stack export
      },
    );
  }
}
